import React from 'react';
import { Modal, Row, Col, Typography, Divider, Space, Tag } from 'antd';
import moment from 'moment';
import { formatStudyDateTime } from '@/shared/utils/studyDate';

import { useGetReportQuery } from '@/state/slices/reports';
import { useGetStudyQuery } from '@/state/slices/studies';

const { Text } = Typography;

interface Props {
  open: boolean;
  onClose: () => void;
  studyUuid?: string | null;
  reportUuid?: string | null;
}

/* ---------- helpers ---------- */
const priorityColor = (p?: string) => {
  if (p === 'STAT') return 'red';
  if (p === 'URGENT') return 'orange';
  if (p === 'ROUTINE') return 'green';
  return 'default';
};

const statusColor = (s?: string | null) => {
  if (s === 'FINAL') return 'green';
  if (s === 'PRELIMINARY') return 'orange';
  return 'blue';
};

const LabelValue: React.FC<{ label: string; value?: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Row style={{ marginBottom: 6 }}>
    <Col span={10}>
      <Text type="secondary">{label}</Text>
    </Col>
    <Col span={14}>{value ?? <Text>—</Text>}</Col>
  </Row>
);

const StudyPropertiesModal: React.FC<Props> = ({
  open,
  onClose,
  studyUuid,
  reportUuid,
}) => {
  const { data: study } = useGetStudyQuery(
    { uuid: studyUuid! },
    { skip: !studyUuid || !open }
  );
  const { data: report } = useGetReportQuery(reportUuid || '', {
    skip: !reportUuid || !open,
  });

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={onClose}
      okText="OK"
      width={760}
      title="Study Properties"
      destroyOnHidden
    >
      {/* ---------- STUDY INFO ---------- */}
      <Divider style={{ textAlign: 'left' }}>Study Info</Divider>

      <Row gutter={24}>
        <Col span={12}>
          <LabelValue label="Study ID" value={study?.uuid} />
          <LabelValue label="Study Type" value={study?.modality} />
          <LabelValue
            label="Report Status"
            value={
              study?.reportStatus ? (
                <Tag color={statusColor(study.reportStatus)}>
                  {study.reportStatus}
                </Tag>
              ) : (
                '—'
              )
            }
          />
          <LabelValue
            label="Date of Service"
            value={formatStudyDateTime(study?.studyDate)}
          />
        </Col>

        <Col span={12}>
          <LabelValue label="ICID" value={study?.icid} />
          <LabelValue label="Site" value={study?.site ?? '—'} />
          <LabelValue
            label="Priority"
            value={
              <Tag color={priorityColor(study?.priority ?? undefined)}>
                {study?.priority ?? '—'}
              </Tag>
            }
          />
        </Col>
      </Row>

      {/* ---------- PATIENT INFO ---------- */}
      <Divider style={{ textAlign: 'left' }}>Patient Info</Divider>

      <Row gutter={24}>
        <Col span={12}>
          <LabelValue
            label="Patient"
            value={
              study ? `${study.patientFirstName} ${study.patientLastName}` : '—'
            }
          />
          <LabelValue label="DOB" value={study?.patientDateOfBirth ?? '—'} />
        </Col>
        <Col span={12}>
          <LabelValue label="MRN" value={study?.patientMedicalRecordNumber} />
          <LabelValue label="Sex" value={study?.patientSex ?? '—'} />
        </Col>
      </Row>

      {/* ---------- REPORT INFO ---------- */}
      <Divider style={{ textAlign: 'left' }}>Report</Divider>

      <Row gutter={24}>
        <Col span={12}>
          <LabelValue
            label="Report Status"
            value={
              report?.status ? (
                <Tag color={statusColor(report.status)}>{report.status}</Tag>
              ) : (
                '—'
              )
            }
          />
          <LabelValue
            label="Report Created"
            value={
              report?.createdAt
                ? moment(report.createdAt).format('MMM D, YYYY, h:mm A')
                : '—'
            }
          />
        </Col>

        <Col span={12}>
          <LabelValue
            label="Signed At"
            value={
              report?.signature?.createdAt
                ? moment(report.signature.createdAt).format(
                    'MMM D, YYYY, h:mm A'
                  )
                : '—'
            }
          />
          <LabelValue
            label="Signed By"
            value={
              report?.signature
                ? `${report.signature.signerFirstName} ${report.signature.signerLastName}`
                : '—'
            }
          />
        </Col>
      </Row>

      {/* ---------- MISC ---------- */}
      <Divider style={{ textAlign: 'left' }}>Miscellaneous</Divider>

      <Space direction="vertical">
        <Text type="secondary">
          This dialog is read-only and reflects the current study & report
          state.
        </Text>
      </Space>
    </Modal>
  );
};

export default StudyPropertiesModal;
